import React, { useState } from 'react';
import { Button } from './button';
import { Download, FileImage, FileSpreadsheet, FileText, Crown } from 'lucide-react';
import { utils, writeFile } from 'xlsx';
import html2canvas from 'html2canvas';
import { useSubscription } from '../../contexts/SubscriptionContext';

const ExportButton = ({ 
  targetId, 
  componentName = 'Component', 
  formats = ['png'], 
  size = 'sm',
  variant = 'outline',
  className = '',
  showIcon = true,
  showText = false,
  checkPermissions = true
}) => {
  const [exporting, setExporting] = useState(false);
  const { subscription } = useSubscription();

  // Check if user can export based on subscription
  const canExport = () => {
    if (!checkPermissions) return true;
    
    console.log('🔍 ExportButton - Checking export permissions:', {
      subscription,
      is_admin: subscription?.is_admin,
      tier: subscription?.tier,
      tier_id: subscription?.tier_id,
      formats
    });
    
    // Admins can always export
    if (subscription?.is_admin || subscription?.tier === 'admin') {
      console.log('✅ Admin detected - Export allowed');
      return true;
    }
    
    const tier = subscription?.tier_id || subscription?.tier || 'starter';
    console.log('📊 Determined tier:', tier);
    
    // Component exports (images) require Professional+
    if (formats.includes('png') || formats.includes('jpeg')) {
      const allowed = ['professional', 'business', 'enterprise'].includes(tier);
      console.log(`${allowed ? '✅' : '❌'} Image export ${allowed ? 'allowed' : 'denied'} for tier:`, tier);
      return allowed;
    }
    
    // Excel exports require Professional+
    if (formats.includes('xlsx') || formats.includes('csv')) {
      const allowed = ['professional', 'business', 'enterprise'].includes(tier);
      console.log(`${allowed ? '✅' : '❌'} Excel export ${allowed ? 'allowed' : 'denied'} for tier:`, tier);
      return allowed;
    }
    
    return true; // Other formats allowed
  };

  const getUpgradeMessage = () => {
    const tier = subscription?.tier_id || 'starter';
    if (tier === 'starter') {
      return 'Upgrade to Professional to export components and charts';
    }
    return 'Upgrade your subscription to access this feature';
  };

  const exportComponent = async (format = 'png') => {
    if (exporting) return;
    
    setExporting(true);
    
    try {
      const element = document.getElementById(targetId);
      if (!element) {
        console.error(`Component ${targetId} not found`);
        return;
      }
      
      const fileName = `${componentName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.${format}`;
      
      if (format === 'png' || format === 'jpeg') {
        // Export as image
        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        });
        
        canvas.toBlob((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, `image/${format}`);
        
      } else if (format === 'xlsx') {
        // Export table as Excel
        const tableElement = element.querySelector('table');
        if (!tableElement) {
          console.error('No table found in component');
          return;
        }
        
        const workbook = utils.table_to_book(tableElement);
        writeFile(workbook, fileName);
        
      } else if (format === 'csv') {
        // Export table as CSV
        const tableElement = element.querySelector('table');
        if (!tableElement) {
          console.error('No table found in component');
          return;
        }
        
        const workbook = utils.table_to_book(tableElement);
        const csvData = utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);
        
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName.replace(`.${format}`, '.csv');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const getIcon = (format) => {
    switch (format) {
      case 'png':
      case 'jpeg':
        return <FileImage className="h-4 w-4" />;
      case 'xlsx':
        return <FileSpreadsheet className="h-4 w-4" />;
      case 'csv':
        return <FileText className="h-4 w-4" />;
      default:
        return <Download className="h-4 w-4" />;
    }
  };

  if (formats.length === 1) {
    // Single format button
    const format = formats[0];
    const hasPermission = canExport();
    
    return (
      <Button
        size={size}
        variant={hasPermission ? variant : 'outline'}
        onClick={hasPermission ? () => exportComponent(format) : undefined}
        disabled={exporting || !hasPermission}
        className={`${className} ${!hasPermission ? 'opacity-60 cursor-not-allowed' : ''}`}
        title={hasPermission ? `Download ${componentName} as ${format.toUpperCase()}` : getUpgradeMessage()}
      >
        {!hasPermission && <Crown className="h-4 w-4 mr-1" />}
        {showIcon && hasPermission && getIcon(format)}
        {showText && <span className="ml-1">{hasPermission ? format.toUpperCase() : 'Upgrade'}</span>}
        {!showIcon && !showText && !hasPermission && <Crown className="h-4 w-4" />}
        {!showIcon && !showText && hasPermission && <Download className="h-4 w-4" />}
      </Button>
    );
  }

  // Multiple format dropdown (could be implemented later)
  const hasPermission = canExport();
  
  return (
    <Button
      size={size}
      variant={hasPermission ? variant : 'outline'}
      onClick={hasPermission ? () => exportComponent(formats[0]) : undefined}
      disabled={exporting || !hasPermission}
      className={`${className} ${!hasPermission ? 'opacity-60 cursor-not-allowed' : ''}`}
      title={hasPermission ? `Download ${componentName}` : getUpgradeMessage()}
    >
      {!hasPermission && <Crown className="h-4 w-4 mr-1" />}
      {showIcon && hasPermission && <Download className="h-4 w-4" />}
      {showText && <span className="ml-1">{hasPermission ? 'Export' : 'Upgrade'}</span>}
    </Button>
  );
};

export default ExportButton;